import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Tag, Progress, Modal, Form, Input, message,
  Typography, Tooltip, Popconfirm, Row, Col, Statistic, Badge, Empty, Switch,
  Drawer, Divider,
} from 'antd';
import {
  PlusOutlined, CopyOutlined, ExportOutlined, DeleteOutlined,
  ReloadOutlined, TeamOutlined, CheckCircleOutlined, ClockCircleOutlined,
  LinkOutlined, EditOutlined, UndoOutlined, EyeOutlined, SettingOutlined,
} from '@ant-design/icons';
import { partnerPortalAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const JJ_ITEMS = [
  { key: '/jj-dashboard', icon: '📊', label: 'JJ Dashboard' },
  { key: '/jj-inbox',     icon: '📨', label: 'JJ Inbox' },
  { key: '/jj-crm',       icon: '📋', label: 'Case Management' },
  { key: '/jj-keywords',  icon: '🔑', label: 'Property Keywords' },
  { key: '/house-ai-rental', icon: '🏠', label: 'Property Search' },
];

const BASE_URL = window.location.origin;

export default function PartnerManagePage() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [jjConfigRecord, setJjConfigRecord] = useState(null);
  const [jjItemsCfg, setJjItemsCfg] = useState({});
  const [jjExpanded, setJjExpanded] = useState(true);
  const [jjSaving, setJjSaving] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const fetchPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnerPortalAPI.list();
      setPartners(res.data || []);
    } catch { message.error('Failed to load partners'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPartners(); }, [fetchPartners]);

  const openJJConfig = (record) => {
    const jjCfg = record.ui_config?.jjproject || {};
    const items = jjCfg.items || {};
    // default all true if not set
    const init = {};
    JJ_ITEMS.forEach(it => { init[it.key] = items[it.key] !== false; });
    setJjItemsCfg(init);
    setJjExpanded(jjCfg.expanded !== false);
    setJjConfigRecord(record);
  };

  const saveJJConfig = async () => {
    if (!jjConfigRecord) return;
    setJjSaving(true);
    try {
      const current = jjConfigRecord.ui_config || {};
      const nextUiConfig = {
        ...current,
        jjproject: {
          ...(current.jjproject || {}),
          visible: true,
          expanded: jjExpanded,
          items: jjItemsCfg,
        },
      };
      await partnerPortalAPI.update(jjConfigRecord.id, { ui_config: nextUiConfig });
      message.success('JJ Project menu saved');
      setJjConfigRecord(null);
      fetchPartners();
    } catch (e) {
      message.error('Save failed: ' + e.message);
    } finally {
      setJjSaving(false);
    }
  };

  const handleCreate = async (values) => {
    try {
      const res = await partnerPortalAPI.create(values);
      const d = res.data;
      message.success(`Portal created for ${d.partner_name}`);
      setCreateOpen(false);
      createForm.resetFields();
      fetchPartners();
      Modal.success({
        title: 'Partner Portal Created',
        content: (
          <div>
            <p><strong>{d.partner_name}</strong></p>
            <p>Portal URL:</p>
            <Input.Group compact>
              <Input value={`${BASE_URL}${d.portal_url}`} readOnly style={{ width: 'calc(100% - 80px)' }} />
              <Button type="primary" onClick={() => { navigator.clipboard.writeText(`${BASE_URL}${d.portal_url}`); message.success('Copied!'); }}>
                Copy
              </Button>
            </Input.Group>
            <p style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
              Share this link with your partner. No login required.
            </p>
          </div>
        ),
      });
    } catch (e) { message.error(e.message || 'Create failed'); }
  };

  const handleEdit = async (values) => {
    try {
      await partnerPortalAPI.update(editRecord.id, values);
      message.success('Updated');
      setEditOpen(false);
      fetchPartners();
    } catch { message.error('Update failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await partnerPortalAPI.remove(id);
      message.success('Deleted');
      fetchPartners();
    } catch { message.error('Delete failed'); }
  };

  const handleReset = async (id) => {
    try {
      await partnerPortalAPI.reset(id);
      message.success('Survey reset');
      fetchPartners();
    } catch { message.error('Reset failed'); }
  };

  // Generic ui_config toggle — flips ui_config[key].visible and PUTs partial update
  const handleToggleUi = async (record, key, visible, label) => {
    setPartners(prev => prev.map(p => p.id === record.id
      ? { ...p, ui_config: { ...(p.ui_config || {}), [key]: { visible } } }
      : p));
    try {
      const nextUiConfig = { ...(record.ui_config || {}), [key]: { visible } };
      await partnerPortalAPI.update(record.id, { ui_config: nextUiConfig });
      message.success(`${label} ${visible ? 'enabled' : 'hidden'}`);
    } catch {
      message.error('Update failed');
      fetchPartners();
    }
  };

  const copyUrl = (portalUrl) => {
    navigator.clipboard.writeText(`${BASE_URL}${portalUrl}`);
    message.success('Link copied!');
  };

  const completedCount = partners.filter(p => p.is_complete).length;
  const inProgressCount = partners.filter(p => !p.is_complete && p.progress_pct > 0).length;

  const columns = [
    {
      title: 'Partner',
      key: 'partner',
      render: (_, r) => (
        <div>
          <Text strong>{r.partner_name}</Text>
          {r.company_name && <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>{r.company_name}</Text>}
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      responsive: ['md'],
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          {r.email && <div>{r.email}</div>}
          {r.phone && <div>{r.phone}</div>}
        </div>
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      width: 160,
      render: (_, r) => (
        <div>
          {r.is_complete ? (
            <Tag color="success" icon={<CheckCircleOutlined />}>Complete</Tag>
          ) : (
            <Progress percent={Math.round(r.progress_pct || 0)} size="small"
              strokeColor={r.progress_pct > 50 ? '#52c41a' : '#D4A03C'} />
          )}
        </div>
      ),
      sorter: (a, b) => (a.progress_pct || 0) - (b.progress_pct || 0),
    },
    {
      title: 'Files',
      dataIndex: 'file_count',
      width: 70,
      align: 'center',
      render: (v) => v > 0 ? <Badge count={v} style={{ background: '#1890ff' }} /> : '-',
    },
    {
      title: 'Login btn',
      key: 'login_toggle',
      width: 80,
      align: 'center',
      render: (_, r) => (
        <Tooltip title={r.ui_config?.login?.visible !== false
          ? 'Login button shown — click to hide'
          : 'Login button hidden — click to show'}>
          <Switch
            size="small"
            checked={r.ui_config?.login?.visible !== false}
            onChange={(v) => handleToggleUi(r, 'login', v, 'Login button')}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Welcome',
      key: 'welcome_toggle',
      width: 80,
      align: 'center',
      render: (_, r) => (
        <Tooltip title={r.ui_config?.welcome?.visible !== false
          ? 'Welcome page shown — click to hide (partner skips straight to first survey section)'
          : 'Welcome page hidden — click to show'}>
          <Switch
            size="small"
            checked={r.ui_config?.welcome?.visible !== false}
            onChange={(v) => handleToggleUi(r, 'welcome', v, 'Welcome page')}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Portal',
      key: 'portal_toggle',
      width: 80,
      align: 'center',
      render: (_, r) => (
        <Tooltip title={r.ui_config?.portal?.visible !== false
          ? 'Portal accessible — click to take offline (visitors see "Portal Not Found")'
          : 'Portal taken offline — visitors see "Portal Not Found". Click to bring back online.'}>
          <Switch
            size="small"
            checked={r.ui_config?.portal?.visible !== false}
            onChange={(v) => handleToggleUi(r, 'portal', v, 'Portal')}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Read Only',
      key: 'readonly_toggle',
      width: 90,
      align: 'center',
      render: (_, r) => (
        <Tooltip title={r.ui_config?.readonly?.visible === true
          ? 'Read-only ON — admin can view but not edit — click to allow editing'
          : 'Editable — click to set read-only'}>
          <Switch
            size="small"
            checked={r.ui_config?.readonly?.visible === true}
            onChange={(v) => handleToggleUi(r, 'readonly', v, 'Read-only')}
          />
        </Tooltip>
      ),
    },
    {
      title: 'JJ Project',
      key: 'jjproject_toggle',
      width: 130,
      align: 'center',
      render: (_, r) => {
        const jjCfg = r.ui_config?.jjproject || {};
        const itemsCfg = jjCfg.items || {};
        const enabledCount = JJ_ITEMS.filter(it => itemsCfg[it.key] !== false).length;
        return (
          <Space size={4}>
            <Tooltip title={jjCfg.visible !== false ? 'JJ menu shown — click to hide' : 'JJ menu hidden — click to show'}>
              <Switch
                size="small"
                checked={jjCfg.visible !== false}
                onChange={(v) => handleToggleUi(r, 'jjproject', v, 'JJ Project menu')}
              />
            </Tooltip>
            <Tooltip title="Configure JJ submenu items">
              <Button
                size="small" type="text" icon={<SettingOutlined />}
                onClick={() => openJJConfig(r)}
                style={{ color: '#8b5cf6' }}
              >
                {enabledCount}/{JJ_ITEMS.length}
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Portal Link',
      key: 'link',
      width: 200,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title={`${BASE_URL}${r.portal_url}`}>
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyUrl(r.portal_url)}>
              Copy
            </Button>
          </Tooltip>
          <Tooltip title="Open portal">
            <Button size="small" icon={<ExportOutlined />}
              onClick={() => window.open(r.portal_url, '_blank')} />
          </Tooltip>
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      width: 110,
      responsive: ['lg'],
      render: (v) => v ? new Date(v).toLocaleDateString('zh-TW') : '-',
      sorter: (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0),
    },
    {
      title: '',
      key: 'actions',
      width: 120,
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button size="small" icon={<EditOutlined />}
              onClick={() => { setEditRecord(r); editForm.setFieldsValue(r); setEditOpen(true); }} />
          </Tooltip>
          <Tooltip title="Reset survey">
            <Popconfirm title="Reset all survey responses?" onConfirm={() => handleReset(r.id)} okText="Reset" cancelText="Cancel">
              <Button size="small" icon={<UndoOutlined />} />
            </Popconfirm>
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm title={`Delete ${r.partner_name}'s portal?`} onConfirm={() => handleDelete(r.id)}
              okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <TeamOutlined style={{ marginRight: 8 }} />Partner Portal Management
          </Title>
          <Text type="secondary">Create and manage partner onboarding portals</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchPartners}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}
            style={{ background: 'linear-gradient(135deg, #D4A03C, #B8860B)', border: 'none' }}>
            New Partner Portal
          </Button>
        </Space>
      </div>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 20 }}>
        <Col xs={8}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title="Total Partners" value={partners.length} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title="In Progress" value={inProgressCount} prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#D4A03C' }} />
          </Card>
        </Col>
        <Col xs={8}>
          <Card size="small" style={{ borderRadius: 10 }}>
            <Statistic title="Completed" value={completedCount} prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Card style={{ borderRadius: 12 }}>
        <Table
          dataSource={partners}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: <Empty description="No partner portals yet. Click 'New Partner Portal' to create one." /> }}
        />
      </Card>

      {/* Create Modal */}
      <Modal
        title="Create New Partner Portal"
        open={createOpen}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        footer={null}
        width={520}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="partner_name" label="Partner Name" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. Janis, David Chen, Tanaka-san" size="large" />
          </Form.Item>
          <Form.Item name="company_name" label="Company Name">
            <Input placeholder="e.g. Bangkok Property Co., Ltd." />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="Email">
                <Input placeholder="partner@example.com" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input placeholder="+66-xxx-xxxx" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="custom_knowledge" label="Custom AI Knowledge (optional)"
            tooltip="Extra context for the AI chatbot — e.g. specific deals, property focus, partner background">
            <Input.TextArea rows={3} placeholder="e.g. This partner focuses on luxury condos in Sukhumvit area, budget range 10-50M THB..." />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit"
                style={{ background: 'linear-gradient(135deg, #D4A03C, #B8860B)', border: 'none' }}>
                Create Portal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        title={`Edit: ${editRecord?.partner_name}`}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        footer={null}
        width={520}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="partner_name" label="Partner Name" rules={[{ required: true }]}>
            <Input size="large" />
          </Form.Item>
          <Form.Item name="company_name" label="Company Name">
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="email" label="Email">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="phone" label="Phone">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="custom_knowledge" label="Custom AI Knowledge">
            <Input.TextArea rows={3} />
          </Form.Item>
          {editRecord && (
            <div style={{
              background: '#f5f5f5', borderRadius: 8, padding: 12, marginBottom: 16,
            }}>
              <Text type="secondary" style={{ fontSize: 12 }}>Portal URL:</Text>
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <Input value={`${BASE_URL}${editRecord.portal_url}`} readOnly size="small" />
                <Button size="small" icon={<CopyOutlined />} onClick={() => copyUrl(editRecord.portal_url)}>
                  Copy
                </Button>
              </div>
            </div>
          )}
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">Save</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* JJ Project submenu config drawer */}
      <Drawer
        title={
          <Space>
            <span>🏙️</span>
            <span>JJ Bangkok — Menu Config</span>
            {jjConfigRecord && <Tag color="blue">{jjConfigRecord.partner_name}</Tag>}
          </Space>
        }
        open={!!jjConfigRecord}
        onClose={() => setJjConfigRecord(null)}
        width={360}
        footer={
          <Space style={{ float: 'right' }}>
            <Button onClick={() => setJjConfigRecord(null)}>Cancel</Button>
            <Button type="primary" loading={jjSaving} onClick={saveJJConfig}>Save</Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>Default State</Text>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8, padding: '10px 12px', background: '#f5f5f5', borderRadius: 8 }}>
            <Text>Expand menu by default</Text>
            <Switch checked={jjExpanded} onChange={setJjExpanded} />
          </div>
        </div>

        <Divider style={{ margin: '12px 0' }}>Submenu Items</Divider>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {JJ_ITEMS.map(item => (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: '#fafafa', borderRadius: 8, border: '1px solid #f0f0f0' }}>
              <Space>
                <span>{item.icon}</span>
                <Text>{item.label}</Text>
              </Space>
              <Switch
                size="small"
                checked={!!jjItemsCfg[item.key]}
                onChange={v => setJjItemsCfg(prev => ({ ...prev, [item.key]: v }))}
              />
            </div>
          ))}
        </div>

        <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
          <Button size="small" block onClick={() => { const all = {}; JJ_ITEMS.forEach(it => { all[it.key] = true; }); setJjItemsCfg(all); }}>Enable All</Button>
          <Button size="small" block onClick={() => { const all = {}; JJ_ITEMS.forEach(it => { all[it.key] = false; }); setJjItemsCfg(all); }}>Disable All</Button>
        </div>
      </Drawer>
    </div>
  );
}
